import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'sharedUi/Dropdown';
import { evalRunService } from '../../services/eval-run.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { EvalCaseChange, EvalCompareCase, EvalCompareRun, EvalRunComparison } from '../../types';
import { EmptyState, SectionLabel, StatusChip } from '../common/panel';
import type { Tone } from '../common/ui-kit';

/** How each change reads, worst news first — the same order the backend sorts in. */
const CHANGE: Record<EvalCaseChange, { label: string; tone: Tone; hint: string }> = {
  regressed:     { label: 'Regressed',     tone: 'danger',  hint: 'Passed before, fails now' },
  fixed:         { label: 'Fixed',         tone: 'success', hint: 'Failed before, passes now' },
  changed:       { label: 'Case edited',   tone: 'warn',    hint: 'The case was edited between the runs, so the results do not compare' },
  errored:       { label: 'Errored',       tone: 'warn',    hint: 'Could not run or score in one of the runs' },
  still_failing: { label: 'Still failing', tone: 'neutral', hint: 'Failed both times' },
  still_passing: { label: 'Still passing', tone: 'neutral', hint: 'Passed both times' },
  new:           { label: 'New',           tone: 'info',    hint: 'Only in this run' },
  removed:       { label: 'Removed',       tone: 'info',    hint: 'Only in the earlier run' },
};
const CHANGE_ORDER = Object.keys(CHANGE) as EvalCaseChange[];
const QUIET: EvalCaseChange[] = ['still_passing', 'still_failing'];
/** A metric that fell by at least this much is named on the row, even if the case still passes. */
const METRIC_DROP = 0.05;

const pct = (v?: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const signed = (v: number | null, scale = 100) =>
  v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(Math.round(v * scale))}`;
const deltaInk = (v: number | null) =>
  v == null || Math.abs(v) < 0.005 ? 'text-ink-muted' : v > 0 ? 'ui-ink ui-ink--good' : 'ui-ink ui-ink--bad';
const ms = (v?: number | null) => (v == null ? null : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${v} ms`);
const money = (v?: number | null, currency?: string | null) =>
  v == null ? null : `${currency ? `${currency} ` : '$'}${v.toFixed(v < 1 ? 4 : 2)}`;
const runLabel = (r: { evalRunId?: string; createdAt?: string | null; runDateTime?: string }) => {
  const when = r.createdAt || r.runDateTime;
  return `${r.evalRunId ?? 'run'}${when ? ` · ${new Date(when).toLocaleString()}` : ''}`;
};

/** Metrics that dropped on this case, worst first. */
const droppedMetrics = (c: EvalCompareCase) => {
  if (!c.base || !c.head) return [];
  return Object.entries(c.head.scores)
    .map(([k, v]) => [k, v - (c.base!.scores[k] ?? v)] as const)
    .filter(([, d]) => d <= -METRIC_DROP)
    .sort((a, b) => a[1] - b[1]);
};

const RunColumn = ({ side, run }: { side: string; run: EvalCompareRun }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">{side}</div>
    <div className="mt-1 truncate font-mono text-xs text-ink" title={run.evalRunId}>{runLabel(run)}</div>
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
      <span>Overall <b className="tabular-nums text-ink">{pct(run.overallScore)}</b></span>
      <span>Pass rate <b className="tabular-nums text-ink">{pct(run.passRate)}</b></span>
      {run.passFail && <StatusChip tone={run.passFail === 'PASS' ? 'success' : 'danger'}>{run.passFail}</StatusChip>}
      {run.modelId && <span className="font-mono">{run.modelId}</span>}
    </div>
    {/* Comparing two models is a trade of quality, speed and spend — the scores
        above are only the first. Absent on runs summarised before these existed. */}
    {(run.latencyP95Ms != null || run.costPerCase != null || (run.trials ?? 1) > 1) && (
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
        {run.latencyP95Ms != null && <span>p95 <b className="tabular-nums text-ink">{ms(run.latencyP95Ms)}</b></span>}
        {run.costPerCase != null && <span>Cost/case <b className="tabular-nums text-ink">{money(run.costPerCase, run.currency)}</b></span>}
        {(run.trials ?? 1) > 1 && <span>{run.trials} trials/case</span>}
      </div>
    )}
  </div>
);

const CaseLine = ({ c }: { c: EvalCompareCase }) => {
  const meta = CHANGE[c.change];
  const drops = droppedMetrics(c);
  const err = c.head?.errorCode || c.base?.errorCode;
  return (
    <li className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] items-start gap-3 px-3 py-2.5" data-testid="EvalCompare.Case">
      <StatusChip tone={meta.tone} className="justify-self-start">{meta.label}</StatusChip>
      <div className="min-w-0">
        <div className="truncate text-sm text-ink" title={c.caseName || c.caseCode}>{c.caseName || c.caseCode}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
          {c.caseName && <span className="font-mono">{c.caseCode}</span>}
          {err && <span className="ui-ink ui-ink--warn font-mono">{err}</span>}
          {drops.map(([k, d]) => (
            <span key={k} className="ui-tone ui-tone--danger">{k}<span className="tabular-nums font-bold">{signed(d)}</span></span>
          ))}
        </div>
      </div>
      <div className="flex items-baseline gap-2 whitespace-nowrap text-sm tabular-nums">
        <span className="text-ink-muted">{pct(c.base?.overallScore)}</span>
        <i className="pi pi-arrow-right text-[10px] text-ink-muted" aria-hidden />
        <span className="font-semibold text-ink">{pct(c.head?.overallScore)}</span>
        <span className={`w-10 text-right text-xs font-semibold ${deltaInk(c.scoreDelta)}`}>{signed(c.scoreDelta)}</span>
      </div>
    </li>
  );
};

interface Props {
  runRef: string;
  suiteRef?: string;
}

/** "Which cases got worse?" — this run against an earlier run of the same suite. */
const EvalRunCompare = ({ runRef, suiteRef }: Props) => {
  const [baseRef, setBaseRef] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { data: history } = useQuery({
    queryKey: ['eval-runs', 'for-compare', suiteRef],
    queryFn: () => evalRunService.listRuns({ suiteRef, hideSingleCase: true, page: 1, pageSize: 50 }),
    enabled: !!suiteRef,
    staleTime: 60 * 1000,
  });
  const baseOptions = useMemo(
    () => (history?.data || [])
      .filter((r) => r._id !== runRef && r.evalRunId !== runRef && (r.status === 'completed' || r.status === 'partial'))
      .map((r) => ({ label: runLabel(r), value: r._id })),
    [history, runRef],
  );

  const { data, isLoading, isError, error } = useQuery<EvalRunComparison>({
    queryKey: ['eval-run-compare', runRef, baseRef],
    queryFn: () => evalRunService.compareRuns(runRef, baseRef),
    retry: false,
  });

  const visible = useMemo(
    () => (data?.cases || []).filter((c) => showAll || !QUIET.includes(c.change)),
    [data, showAll],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="EvalCompare">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-[16rem] flex-col gap-1">
          <label htmlFor="eval-compare-base" className="text-xs font-medium text-ink-muted">Compare with</label>
          <Dropdown
            inputId="eval-compare-base"
            value={baseRef}
            options={baseOptions}
            onChange={(e: any) => setBaseRef(e.value || null)}
            placeholder="Previous full run of this suite"
            showClear
            filter={baseOptions.length > 8}
          />
        </div>
        {data && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-muted">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Show unchanged cases ({data.counts.still_passing + data.counts.still_failing})
          </label>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><ProgressSpinner style={{ width: '28px', height: '28px' }} strokeWidth="5" /></div>
      ) : isError || !data ? (
        <EmptyState icon="pi pi-clone" title="Nothing to compare yet"
                    hint={extractErrorMessage(error, 'This run could not be compared.')} />
      ) : (
        <>
          <div className="ui-inset grid gap-4 p-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
            <RunColumn side="Earlier run" run={data.base} />
            <div className="flex flex-col items-center text-xs text-ink-muted">
              <i className="pi pi-arrow-right" aria-hidden />
              <span className={`mt-1 tabular-nums font-semibold ${deltaInk(data.overallScoreDelta)}`}>
                {signed(data.overallScoreDelta)} pts
              </span>
              {/* Lower is better for both, so the ink is inverted. */}
              {data.latencyP95DeltaMs != null && (
                <span className={`tabular-nums ${deltaInk(-data.latencyP95DeltaMs)}`} title="Change in p95 latency">
                  {data.latencyP95DeltaMs > 0 ? '+' : data.latencyP95DeltaMs < 0 ? '−' : '±'}{ms(Math.abs(data.latencyP95DeltaMs))}
                </span>
              )}
              {data.costPerCaseDelta != null && Math.abs(data.costPerCaseDelta) > 0 && (
                <span className={`tabular-nums ${deltaInk(-data.costPerCaseDelta)}`} title="Change in cost per case">
                  {data.costPerCaseDelta > 0 ? '+' : '−'}{money(Math.abs(data.costPerCaseDelta), data.head.currency)}/case
                </span>
              )}
            </div>
            <RunColumn side="This run" run={data.head} />
          </div>

          {!data.sameSuite && (
            <p className="ui-ink ui-ink--warn text-xs">These runs are from different suites, so only shared cases line up.</p>
          )}
          {data.sameSuite && data.sameScoring === false && (
            <p className="ui-ink ui-ink--warn text-xs">These runs were scored under different rules (pass bar, metrics or weights), so a case that got worse may only have met a stricter rule.</p>
          )}

          <div className="flex flex-wrap gap-1.5" aria-label="What changed">
            {CHANGE_ORDER.filter((k) => data.counts[k] > 0).map((k) => (
              <StatusChip key={k} tone={CHANGE[k].tone} className="tabular-nums">
                <span title={CHANGE[k].hint}>{CHANGE[k].label} {data.counts[k]}</span>
              </StatusChip>
            ))}
          </div>

          {Object.keys(data.metricDeltas).length > 0 && (
            <div>
              <SectionLabel icon="pi pi-chart-bar">Metric averages</SectionLabel>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {Object.entries(data.metricDeltas).map(([k, d]) => (
                  <span key={k} className="ui-tone ui-tone--neutral">
                    {k}
                    <span className="tabular-nums">{pct(data.base.metricSummary[k])} → {pct(data.head.metricSummary[k])}</span>
                    <span className={`tabular-nums font-bold ${deltaInk(d)}`}>{signed(d)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {visible.length > 0 ? (
            <ul className="ui-panel divide-y divide-gray-100 overflow-hidden dark:divide-gray-700/60">
              {visible.map((c) => <CaseLine key={c.caseRef} c={c} />)}
            </ul>
          ) : (
            <EmptyState icon="pi pi-check-circle" title="No case changed its outcome"
                        hint="Every case passed or failed the same way in both runs." />
          )}
        </>
      )}
    </div>
  );
};

export default EvalRunCompare;
